import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type KeyboardEvent } from 'react';
import {
  applyRowEvent,
  enterSettlesTime,
  rowMessages,
  timeBoxValue,
  type RowEvent,
  type RowState,
} from '../lib/linked-time-fields';
import { FieldError } from './field-error';

/**
 * The `Duration | Start | End` fields, linked, shared by Log Time and Edit Time
 * Entry so the two cannot drift. All three are always on screen.
 *
 * Start and End sat behind a "Set start and end time" link at first, per the
 * ticket's own scope, with the shown-or-hidden choice remembered per browser.
 * The Tempo screenshot the ticket links shows all four fields at once, and the
 * first person handed the branch asked where the times were — so the link went,
 * agreed at the review of 2026-09-09. [HW-891]
 */

interface TimeFieldsRowProps {
  /**
   * The whole row, the dialog's to own — not just the three linked values.
   *
   * The half-typed text and the not-yet-settled Duration used to be this
   * component's private state, which meant a submit handler could not settle
   * them and read what the reader had actually typed. It could not even see
   * them. They live in the dialog now, and both submit paths pass the row
   * through `settleRow` first. [HW-891]
   */
  row: RowState;
  onChange: (next: RowState) => void;
  /** Run before the change lands — the dialogs clear a standing save error the
   *  moment the reader touches a field. */
  onEdit?: () => void;
  /** Keeps the field ids distinct when both dialogs mount (they never do at
   *  once, but a stable, unique id is cheap). */
  idPrefix?: string;
  /** Compact spacing + labels for the tighter Edit dialog. */
  dense?: boolean;
}

export function TimeFieldsRow({
  row,
  onChange,
  onEdit,
  idPrefix = 'log',
  dense = false,
}: TimeFieldsRowProps) {
  const state = row.linked;

  /**
   * Hand one event to the row and put back only what actually moved — the
   * reducer returns the very same row when nothing did, so a settle that settles
   * nothing costs no render and, more to the point, cannot overwrite the parent's
   * state with a relink it never asked for.
   */
  const dispatch = (event: RowEvent) => {
    const next = applyRowEvent(row, event);
    if (next !== row) onChange(next);
  };

  /** Which sentence goes under which box is `rowMessages`' business, where it
   *  can be tested; this component only places them. */
  const messages = rowMessages(row);

  // `block mb-1.5` because a bare `<label>` is inline and carries no margin:
  // without it the dense fields sat flush against their captions while the Date
  // and Description boxes around them kept their 6px. The `Label` component
  // Log Time uses brings its own spacing.
  const labelClass = dense ? 'block mb-1.5 text-xs font-medium text-muted-foreground' : undefined;
  const inputClass = dense ? 'h-9 text-sm' : undefined;

  /** Enter settles the box it is pressed in rather than submitting the form
   *  around it; see `enterSettlesTime` for what that saves. */
  const settleOnEnter = (event: KeyboardEvent<HTMLInputElement>, field: 'start' | 'end') => {
    if (event.key !== 'Enter' || !enterSettlesTime(row, field)) return;
    event.preventDefault();
    dispatch({ kind: 'settle-time', field });
  };

  return (
    <div className="space-y-2">
      {/* `pb-3`, not the `pb-1` this block carried in the form it came from:
          `FieldError` needs ~19px under the input and the surrounding gap is
          now `space-y-2` rather than `space-y-4`, so 4 + 8 left the message
          4px over the "Start time" label — measured, not guessed. */}
      <div className="pb-3">
        <div className="relative">
          {dense ? (
            <label htmlFor={`${idPrefix}-duration`} className={labelClass}>
              Duration
            </label>
          ) : (
            <Label htmlFor={`${idPrefix}-duration`}>Duration</Label>
          )}
          <Input
            id={`${idPrefix}-duration`}
            inputMode="text"
            placeholder="0h"
            autoComplete="off"
            className={inputClass}
            aria-invalid={!!messages.duration}
            value={state.duration}
            onChange={(e) => {
              onEdit?.();
              // Text lands immediately; the interval is relinked once it settles.
              dispatch({ kind: 'type-duration', text: e.target.value });
            }}
            onBlur={() => dispatch({ kind: 'settle-duration' })}
            autoFocus={!dense}
          />
          {messages.duration && <FieldError>{messages.duration}</FieldError>}
        </div>
      </div>

      <div className="flex items-end gap-3 pb-1">
        <div className="flex-1">
          {dense ? (
            <label htmlFor={`${idPrefix}-start`} className={labelClass}>
              Start time
            </label>
          ) : (
            <Label htmlFor={`${idPrefix}-start`}>Start time</Label>
          )}
          <Input
            id={`${idPrefix}-start`}
            inputMode="numeric"
            autoComplete="off"
            className={inputClass}
            value={timeBoxValue(row, 'start')}
            onChange={(e) => {
              onEdit?.();
              dispatch({ kind: 'type-time', field: 'start', text: e.target.value });
            }}
            onKeyDown={(e) => settleOnEnter(e, 'start')}
            onBlur={() => dispatch({ kind: 'settle-time', field: 'start' })}
          />
        </div>
        {/* The only time box that ever explains itself — hence the `relative`
            wrapper `FieldError` needs, which the Start box has no use for. */}
        <div className="flex-1">
          <div className="relative">
            {dense ? (
              <label htmlFor={`${idPrefix}-end`} className={labelClass}>
                End time
              </label>
            ) : (
              <Label htmlFor={`${idPrefix}-end`}>End time</Label>
            )}
            <Input
              id={`${idPrefix}-end`}
              inputMode="numeric"
              autoComplete="off"
              className={inputClass}
              aria-invalid={!!messages.end}
              value={timeBoxValue(row, 'end')}
              onChange={(e) => {
                onEdit?.();
                dispatch({ kind: 'type-time', field: 'end', text: e.target.value });
              }}
              onKeyDown={(e) => settleOnEnter(e, 'end')}
              onBlur={() => dispatch({ kind: 'settle-time', field: 'end' })}
            />
            {messages.end && <FieldError>{messages.end}</FieldError>}
          </div>
        </div>
      </div>
    </div>
  );
}
